import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { SlidersHorizontal, X } from "lucide-react";
import { useTags } from "@/hooks/useTags";
import { useTeam } from "@/hooks/useTeam";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  canFilterChatByOtherMembers,
  canSeeChatOwnershipTabUnassigned,
} from "@/lib/chatContactScope";

export interface ChatFilterValues {
  attributionId: string | null;
  statusChat: string | null;
  tag: string | null;
}

interface ChatFiltersProps {
  filters: ChatFilterValues;
  onFiltersChange: (filters: ChatFilterValues) => void;
}

const defaultFilters: ChatFilterValues = {
  attributionId: null,
  statusChat: "aberto",
  tag: null,
};

export const ChatFilters = ({ filters, onFiltersChange }: ChatFiltersProps) => {
  const { t } = useTranslation("chat");
  const { user } = useAuth();
  const { tags: accountTags, loading: loadingTags } = useTags();
  const { members, loading: loadingMembers } = useTeam();
  const showOtherMembersInFilter = canFilterChatByOtherMembers(user);
  const showUnassignedInFilter = canSeeChatOwnershipTabUnassigned(user);
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<ChatFilterValues>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const activeFiltersCount = [
    filters.tag,
    filters.attributionId,
  ].filter(Boolean).length;

  const handleApply = () => {
    onFiltersChange(localFilters);
    setIsOpen(false);
  };

  const handleClear = () => {
    const cleared = { ...defaultFilters, statusChat: filters.statusChat, attributionId: filters.attributionId };
    setLocalFilters(cleared);
    onFiltersChange(cleared);
    setIsOpen(false);
  };

  const handleSelectChange = (key: keyof ChatFilterValues, value: string) => {
    const newValue = value === "__all__" ? null : value;
    setLocalFilters(prev => ({ ...prev, [key]: newValue }));
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-[36px] px-3 border border-border bg-card hover:bg-hover transition-colors duration-200 rounded-[12px] flex items-center justify-center gap-2 shadow-sm dark:border-neutral-500 dark:bg-neutral-600 dark:hover:bg-neutral-500 dark:shadow-none">
          <SlidersHorizontal className="w-4 h-4 text-muted-foreground dark:text-neutral-300" />
          {activeFiltersCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center !bg-primary hover:!bg-primary !text-primary-foreground !border-0 text-xs">
              {activeFiltersCount}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] bg-card border-border p-4 dark:bg-neutral-800 dark:border-neutral-700" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground dark:text-neutral-100">{t("filters.title")}</span>
            {activeFiltersCount > 0 && (
              <button
                onClick={handleClear}
                className="text-xs text-muted-foreground hover:text-foreground dark:text-neutral-400 dark:hover:text-neutral-200 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                {t("filters.clear")}
              </button>
            )}
          </div>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground dark:text-neutral-400">{t("filters.assignedUser")}</label>
              <Select
                value={localFilters.attributionId || "__all__"}
                onValueChange={(v) => handleSelectChange("attributionId", v)}
              >
                <SelectTrigger className="h-9 bg-input border-border text-sm dark:bg-neutral-700 dark:border-neutral-600">
                  <SelectValue placeholder={loadingMembers ? t("common:shared.loadingShort") : t("filters.allMembers")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="__all__">{t("filters.all")}</SelectItem>
                    {showUnassignedInFilter && (
                      <SelectItem value="__unassigned__">{t("filters.unassigned")}</SelectItem>
                    )}
                    {members
                      .filter(m => m.is_active && m.auth_user_id)
                      .filter(m =>
                        showOtherMembersInFilter ? true : m.auth_user_id === user?.auth_user_id
                      )
                      .map((member) => (
                      <SelectItem key={member.id} value={member.auth_user_id!}>
                        {member.name || member.email}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground dark:text-neutral-400">{t("filters.tag")}</label>
              <Select
                value={localFilters.tag || "__all__"}
                onValueChange={(v) => handleSelectChange("tag", v)}
              >
                <SelectTrigger className="h-9 bg-input border-border text-sm dark:bg-neutral-700 dark:border-neutral-600">
                  <SelectValue placeholder={loadingTags ? "Carregando..." : "Todas"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="__all__">{t("filters.allF")}</SelectItem>
                    {accountTags.filter(t => t.is_active).map((tag) => (
                      <SelectItem key={tag.id} value={tag.name}>
                        {tag.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={handleApply} className="w-full h-9 text-sm">
            {t("filters.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { defaultFilters as defaultChatFilters };
